import React, { useState, useEffect } from 'react';
import api from '../services/api';

const ACTION_GROUPS = [
  { label: 'Alle',             value: '' },
  { label: 'Login',            value: 'login.success,login.failed,login.locked_out,login.logout' },
  { label: 'Klantportaal',     value: 'portal.login.success,portal.login.failed,portal.login.locked_out' },
  { label: 'Gebruikersbeheer', value: 'user.created,user.updated,user.activated,user.deactivated,user.sessions.revoked' },
  { label: 'Werkorders',       value: 'werkorder.created,werkorder.updated,werkorder.deleted,werkorder.doorgestuurd' },
  { label: 'Forms',            value: 'form.submission.created,form.submission.deleted,form.pdf.downloaded,form.mail.clicked' },
];

const th = { padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 };
const td = { padding: '8px 12px', verticalAlign: 'top' };

const detailBadge = (bg, fg) => ({
  display: 'inline-block', background: bg, color: fg,
  padding: '2px 8px', borderRadius: 10, fontSize: 11,
  fontWeight: 600, marginRight: 4, marginBottom: 2,
  whiteSpace: 'nowrap',
});

function renderDetailBadges(action, details) {
  if (!details || typeof details !== 'object' || Object.keys(details).length === 0) return null;
  const out = [];
  const add = (bg, fg, txt) => out.push([bg, fg, txt]);

  if (action === 'user.sessions.revoked') {
    if (details.gebruikersnaam) add('#fef3c7', '#92400e', '👤 ' + details.gebruikersnaam);
    if (details.ingetrokken !== undefined) add('#fee2e2', '#991b1b', '🚫 ' + details.ingetrokken + ' sessie' + (details.ingetrokken === 1 ? '' : 's'));
    if (details.nieuwe_token_versie !== undefined) add('#dbeafe', '#1e40af', '🔢 v' + details.nieuwe_token_versie);
  } else if (action === 'login.failed') {
    if (details.gebruikersnaam) add('#fef3c7', '#92400e', '👤 ' + details.gebruikersnaam);
    if (details.reden) add('#fee2e2', '#991b1b', details.reden);
  } else if (action === 'login.locked_out') {
    if (details.gebruikersnaam) add('#fee2e2', '#991b1b', '🔒 ' + details.gebruikersnaam);
    if (details.aantal_pogingen !== undefined) add('#fecaca', '#7f1d1d', details.aantal_pogingen + '× pogingen');
    if (details.venster_min !== undefined) add('#fed7aa', '#9a3412', 'binnen ' + details.venster_min + ' min');
  } else if (action === 'login.success' || action === 'login.logout') {
    if (details.gebruikersnaam) add('#dcfce7', '#166534', '👤 ' + details.gebruikersnaam);
  } else if (action && action.startsWith('werkorder.')) {
    if (details.werkorder_id) add('#d1fae5', '#065f46', '#' + details.werkorder_id);
    if (details.fields && Array.isArray(details.fields)) add('#ecfccb', '#365314', details.fields.join(', ').slice(0, 40));
  } else if (action && action.startsWith('user.')) {
    if (details.gebruikersnaam) add('#fef3c7', '#92400e', '👤 ' + details.gebruikersnaam);
    if (details.fields && Array.isArray(details.fields)) add('#ecfccb', '#365314', details.fields.join(', ').slice(0, 40));
  } else {
    const keys = Object.keys(details).slice(0, 2);
    keys.forEach(k => add('#e5e7eb', '#374151', k + ': ' + String(details[k]).slice(0, 20)));
    if (Object.keys(details).length > 2) add('#f3f4f6', '#6b7280', '+' + (Object.keys(details).length - 2));
  }

  return out.map(([bg, fg, txt], i) => (
    <span key={i} style={detailBadge(bg, fg)}>{txt}</span>
  ));
}


function AuditTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [filterHours, setFilterHours] = useState('168');
  const [filterUser, setFilterUser] = useState('');
  const [limit, setLimit] = useState(100);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { loadEvents(); /* eslint-disable-next-line */ }, [filterAction, filterHours, limit]);

  async function loadEvents() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      if (filterHours) params.append('hours', filterHours);
      if (filterAction) filterAction.split(',').forEach(a => params.append('action', a.trim()));
      const res = await api.get('/audit/recent?' + params.toString());
      setEvents(res.data.data || []);
    } catch (err) {
      console.error('Fout bij ophalen audit log:', err);
      setEvents([]);
    } finally { setLoading(false); }
  }

  const filtered = filterUser
    ? events.filter(e =>
        (e.actor_naam || '').toLowerCase().includes(filterUser.toLowerCase()) ||
        (e.actor_gebruikersnaam || '').toLowerCase().includes(filterUser.toLowerCase())
      )
    : events;

  const formatDate = (d) => d ? new Date(d).toLocaleString('nl-NL') : '-';

  const actionBadge = (action) => {
    // Alarm-styling voor lockout: rood ipv standaard login-blauw
    if (action === 'login.locked_out') {
      return { background: '#fecaca', color: '#7f1d1d', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 700 };
    }
    const cat = (action || '').split('.')[0];
    const colors = {
      login:     { bg: '#dbeafe', fg: '#1e40af' },
      user:      { bg: '#fef3c7', fg: '#92400e' },
      werkorder: { bg: '#d1fae5', fg: '#065f46' },
      form:      { bg: '#fce7f3', fg: '#831843' },
    };
    const c = colors[cat] || { bg: '#e5e7eb', fg: '#374151' };
    return { background: c.bg, color: c.fg, padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 };
  };

  return (
    <div style={{ padding: 20 }}>
      <h3 style={{ marginTop: 0, marginBottom: 16 }}>📋 Audit log</h3>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Categorie</label>
          <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}>
            {ACTION_GROUPS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Periode</label>
          <select value={filterHours} onChange={e => setFilterHours(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}>
            <option value="24">Laatste 24u</option>
            <option value="168">Laatste 7 dagen</option>
            <option value="720">Laatste 30 dagen</option>
            <option value="">Alles</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Gebruiker</label>
          <input type="text" value={filterUser} onChange={e => setFilterUser(e.target.value)} placeholder="naam of username" style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, width: 180 }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Aantal</label>
          <select value={limit} onChange={e => setLimit(parseInt(e.target.value))} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="250">250</option>
            <option value="500">500</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button onClick={loadEvents} disabled={loading} style={{ padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
            {loading ? '⏳' : '🔄 Vernieuw'}
          </button>
        </div>
      </div>

      <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 8 }}>
        {filtered.length} {filtered.length === 1 ? 'gebeurtenis' : 'gebeurtenissen'}
        {filterUser && ` (gefilterd op "${filterUser}")`}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>⏳ Laden…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Geen events gevonden</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={th}>Tijdstip</th>
                <th style={th}>Gebruiker</th>
                <th style={th}>Actie</th>
                <th style={th}>Target</th>
                <th style={th}>IP</th>
                <th style={th}>Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <React.Fragment key={e.id}>
                  <tr style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={td}>{formatDate(e.created_at)}</td>
                    <td style={td}>{e.actor_naam || e.actor_gebruikersnaam || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    <td style={td}><span style={actionBadge(e.action)}>{e.action}</span></td>
                    <td style={td}>
                      {e.target_type ? (
                        <span style={{ color: '#6b7280' }}>{e.target_type}{e.target_id ? ' #' + e.target_id : ''}</span>
                      ) : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12, color: '#6b7280' }}>{e.ip_address || '—'}</td>
                    <td style={td}>
                      {renderDetailBadges(e.action, e.details)}
                      {e.details && typeof e.details === 'object' && Object.keys(e.details).length > 0 && (
                        <button onClick={() => setExpanded(expanded === e.id ? null : e.id)} style={{ background: 'transparent', border: '1px solid #d1d5db', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', fontSize: 11, marginLeft: 4 }} title="JSON tonen">
                          {expanded === e.id ? '▾' : '▸'}
                        </button>
                      )}
                      {(!e.details || typeof e.details !== 'object' || Object.keys(e.details).length === 0) && <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                  </tr>
                  {expanded === e.id && e.details && (
                    <tr><td colSpan="6" style={{ padding: 12, background: '#f9fafb', fontFamily: 'monospace', fontSize: 12, color: '#374151' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(e.details, null, 2)}</pre>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AuditTab;
